/**
 * People and how to reach them: initials for an avatar, and the links a phone
 * opens with one tap.
 */

/**
 * Up to two initials for the avatar chip.
 *
 * Takes the first letter of the first and last words, so "Rosa María Pérez"
 * reads as "RP" rather than "RM" — the surname is what distinguishes two
 * clients called Rosa María.
 */
export const initials = (fullName: string): string => {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${first}${last}`.toUpperCase();
};

/** Digits only, for the `tel:` link — the stored form may carry a `+`. */
export const telHref = (phone: string): string => `tel:${phone.replace(/[^\d+]/g, '')}`;

/**
 * Country code assumed for numbers typed the local way.
 *
 * The business runs in Ecuador (`es-EC`, `America/Guayaquil`, USD), where a
 * mobile number is written `09…`. WhatsApp needs it as `5939…`.
 */
export const DEFAULT_COUNTRY_CODE = '593';

/**
 * The number WhatsApp expects: country code and digits, no `+`, no leading 0.
 *
 * Returns null when the number is too short to be a phone, so a link that
 * would open WhatsApp on a nonsense chat is not offered at all.
 */
export const whatsappNumber = (phone: string): string | null => {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 7) return null;
  if (trimmed.startsWith('+')) return digits;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('0')) return `${DEFAULT_COUNTRY_CODE}${digits.slice(1)}`;
  if (digits.startsWith(DEFAULT_COUNTRY_CODE) && digits.length > 10) return digits;
  return `${DEFAULT_COUNTRY_CODE}${digits}`;
};

/**
 * A WhatsApp click-to-chat link, optionally with the message already written.
 *
 * Straight to `api.whatsapp.com/send`, not through `wa.me`: `wa.me` answers
 * with a redirect to that same address and, on the way, replaces every
 * character of three or more UTF-8 bytes with U+FFFD — so 👋, ⏰ or 🎂 reach
 * the chat as "�" while "ñ" survives. Going to the redirect's target directly
 * keeps the text exactly as written.
 */
export const whatsappHref = (phone: string, text?: string): string | null => {
  const number = whatsappNumber(phone);
  if (!number) return null;
  const base = `https://api.whatsapp.com/send?phone=${number}`;
  return text ? `${base}&text=${encodeURIComponent(text)}` : base;
};
